import { useState, useEffect } from 'react';
import { TIER_LABELS } from '@mockd/shared';
import type { Rule, CreateRuleRequest, UpdateRuleRequest } from '@mockd/shared';
import type { Condition, ResponseStep, ActionDefinition } from '@mockd/shared/types/rule';
import { useRules } from '../../hooks/useRules';
import { RuleList } from './RuleList';
import { RuleForm } from './RuleForm';
import { RuleTemplates } from './RuleTemplates';
import { ImportRulesModal } from './ImportRulesModal';
import { downloadJson } from '../../utils/exportEndpoint';
import { Alert, ConfirmDialog, LimitIndicator, Loading, READ_ONLY_HINT } from '../common';

interface RulesPanelProps {
  projectId: string;
  endpointId: string;
  /** The caller's `permissions.canWrite` on the project; false shows the rules read-only. */
  canWrite: boolean;
}

export interface RuleFormData {
  name: string;
  description: string;
  conditions: Condition[];
  response: {
    steps: ResponseStep[];
    sequenceMode: 'loop' | 'hold_last';
  } | null;
  actions: ActionDefinition[] | null;
}

type FormMode =
  | { type: 'closed' }
  | { type: 'template' }
  | { type: 'create' }
  | { type: 'edit'; rule: Rule };

export function RulesPanel({ projectId, endpointId, canWrite }: RulesPanelProps) {
  const {
    rules,
    loading,
    error,
    ruleLimit,
    // The project's effective tier, straight from the worker that enforces the cap —
    // not the viewer's own tier, which is wrong for a member of someone else's team.
    tier,
    fetchRules,
    createRule,
    updateRule,
    deleteRule,
    reorderRules,
    duplicateRule,
    resetRuleSequence,
    exportRules,
    importRules,
  } = useRules();
  const [formMode, setFormMode] = useState<FormMode>({ type: 'closed' });
  const [showImport, setShowImport] = useState(false);
  const [deleteRuleId, setDeleteRuleId] = useState<string | null>(null);
  const [initialData, setInitialData] = useState<Partial<RuleFormData> | null>(null);

  const atLimit = ruleLimit !== null && rules.length >= ruleLimit;
  const limitReason = atLimit
    ? `Rule limit reached — the ${TIER_LABELS[tier ?? 'free']} plan allows ${ruleLimit} rules per endpoint. Upgrade to add more.`
    : undefined;
  // Read-only outranks the limit: upgrading wouldn't let this caller add a rule.
  const createDisabled = !canWrite || atLimit;
  const createReason = canWrite ? limitReason : READ_ONLY_HINT;

  useEffect(() => {
    fetchRules(projectId, endpointId);
  }, [projectId, endpointId, fetchRules]);

  const handleCreate = async (data: CreateRuleRequest | UpdateRuleRequest) => {
    await createRule(projectId, endpointId, data as Omit<CreateRuleRequest, 'endpointId'>);
    setFormMode({ type: 'closed' });
    setInitialData(null);
  };

  const handleUpdate = async (data: CreateRuleRequest | UpdateRuleRequest) => {
    if (formMode.type !== 'edit') return;
    await updateRule(projectId, endpointId, formMode.rule.id, data as UpdateRuleRequest);
    setFormMode({ type: 'closed' });
  };

  const handleDelete = (ruleId: string) => {
    setDeleteRuleId(ruleId);
  };

  const confirmDelete = async () => {
    if (!deleteRuleId) return;
    await deleteRule(projectId, endpointId, deleteRuleId);
    setDeleteRuleId(null);
  };

  const handleToggleActive = async (rule: Rule) => {
    await updateRule(projectId, endpointId, rule.id, { isActive: !rule.isActive });
  };

  const handleReorder = async (ruleIds: string[]) => {
    await reorderRules(projectId, endpointId, ruleIds);
  };

  const handleDuplicate = async (ruleId: string) => {
    await duplicateRule(projectId, endpointId, ruleId);
  };

  const handleResetSequence = async (ruleId: string) => {
    await resetRuleSequence(projectId, endpointId, ruleId);
  };

  const handleExport = async () => {
    try {
      const exported = await exportRules(projectId, endpointId);
      downloadJson({ rules: exported }, `rules-${endpointId}.json`);
    } catch {
      // useRules has already reported the failure
    }
  };

  const handleTemplateSelect =(data: Partial<RuleFormData>) => {
    setInitialData(data);
    setFormMode({ type: 'create' });
  };

  const handleCancel = () => {
    setFormMode({ type: 'closed' });
    setInitialData(null);
  };

  if (loading && rules.length === 0) {
    return (
      <div className="card bg-base-100">
        <div className="px-4 py-3 border-b border-base-200">
          <h2>Rules</h2>
        </div>
        <Loading label="Loading rules…" className="px-4 py-12" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="card bg-base-100">
        <div className="px-4 py-3 border-b border-base-200">
          <h2>Rules</h2>
        </div>
        <div className="p-4">
          <Alert variant="error">{error}</Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="card bg-base-100">
      <div className="px-4 py-3 border-b border-base-200 flex items-center justify-between">
        <h2>Rules</h2>
        {formMode.type === 'closed' && (
          <div className="flex items-center gap-1">
            <button
              onClick={handleExport}
              disabled={rules.length === 0}
              className="btn btn-ghost btn-sm"
            >
              Export
            </button>
            <button
              onClick={() => setShowImport(true)}
              disabled={createDisabled}
              className="btn btn-ghost btn-sm"
              title={createReason}
            >
              Import
            </button>
            <button
              onClick={() => setFormMode({ type: 'template' })}
              disabled={createDisabled}
              className="btn btn-ghost btn-sm"
              title={createReason}
            >
              Add Rule
            </button>
          </div>
        )}
      </div>

      {ruleLimit !== null && (
        <div className="px-4 py-3 border-b border-base-200">
          <LimitIndicator used={rules.length} max={ruleLimit} label="Rules" />
        </div>
      )}

      {formMode.type === 'template' && (
        <div className="p-4">
          <RuleTemplates
            onSelect={handleTemplateSelect}
            onCancel={handleCancel}
          />
        </div>
      )}

      {(formMode.type === 'create' || formMode.type === 'edit') && (
        <div className="p-4">
          <h3 className="mb-4">
            {formMode.type === 'create' ? 'Create New Rule' : 'Edit Rule'}
          </h3>
          <RuleForm
            rule={formMode.type === 'edit' ? formMode.rule : undefined}
            initialData={formMode.type === 'create' ? initialData ?? undefined : undefined}
            tier={tier ?? 'free'}
            onSubmit={formMode.type === 'create' ? handleCreate : handleUpdate}
            onCancel={handleCancel}
          />
        </div>
      )}

      {formMode.type === 'closed' && (
        <>
          <RuleList
            rules={rules}
            onEdit={(rule) => setFormMode({ type: 'edit', rule })}
            onDelete={handleDelete}
            onToggleActive={handleToggleActive}
            onReorder={handleReorder}
            onDuplicate={handleDuplicate}
            onResetSequence={handleResetSequence}
            atLimit={atLimit}
            limitReason={limitReason}
            readOnly={!canWrite}
          />
          {rules.length === 0 && (
            <div className="px-4 py-2 text-xs text-base-content/70 border-t border-base-200">
              When no rules match, the endpoint&apos;s default response is used.
            </div>
          )}
        </>
      )}

      <ConfirmDialog
        isOpen={deleteRuleId !== null}
        title="Delete Rule"
        message="Are you sure you want to delete this rule?"
        confirmText="Delete"
        variant="danger"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteRuleId(null)}
      />

      <ImportRulesModal
        isOpen={showImport}
        onClose={() => setShowImport(false)}
        onImport={(imported) => importRules(projectId, endpointId, imported)}
      />
    </div>
  );
}
